<template>
  <header>
    <nav class="navbar navbar-expand-md navbar-light">
      <div class="container-fluid">
        <router-link to="/" class="navbar-brand">
          <img
            :src="$t('Header.BrandLogo')"
            alt="Brand Logo"
            class="img-fluid"
          />
        </router-link>

        <!-- Language Dropdown Mobile -->
        <div class="lang-dropdown dropdown d-md-none ms-auto me-3" @mouseenter="showDropdownMobile = true" @mouseleave="showDropdownMobile = false">
          <button 
            class="lang-dropdown-btn d-flex align-items-center"
            @click="toggleDropdownMobile"
            aria-expanded="false"
          >
            <i class="fa-solid fa-globe me-2"></i>
            <span>{{ currentLocale.toUpperCase() }}</span>
            <i class="fa-solid fa-chevron-down ms-2 lang-chevron" :class="{ rotated: showDropdownMobile }"></i>
          </button>
          <ul class="dropdown-menu lang-dropdown-menu" :class="{ show: showDropdownMobile }">
            <li>
              <button class="dropdown-item" :class="{ active: currentLocale === 'en' }" @click="selectLangMobile('en')">EN</button>
            </li>
            <li>
              <button class="dropdown-item" :class="{ active: currentLocale === 'fr' }" @click="selectLangMobile('fr')">FR</button>
            </li>
          </ul>
        </div>

        <button
          class="navbar-toggler"
          type="button"
          data-toggle="collapse"
          data-target="#navbar-Nav"
          @click="toggleCollapse"
          aria-controls="navbar-Nav"
          aria-expanded="false"
          aria-label="Toggle navigation"
          :class="{ active: isCollapsed }"
        ></button>

        <div
          :class="{ show: isCollapsed }"
          class="collapse navbar-collapse my-2"
          id="navbar-Nav"
        >
          <ul class="navbar-nav ms-md-auto">
            <li class="nav-item">
              <router-link
                to="/Home"
                class="nav-link"
                active-class="active"
                @click="closeMenu"
              >
                {{ $t('Header.HomeTab') }}
              </router-link>
            </li>

            <li class="nav-item">
              <router-link
                to="/SignIn"
                class="nav-link"
                active-class="active"
                @click="closeMenu"
              >
                {{ $t('Header.SignInTab') }}
              </router-link>
            </li>

            <li class="nav-item">
              <router-link to="/FAQ" class="nav-link" @click="closeMenu">
                {{ $t('Header.SupportTab') }}
              </router-link>
            </li>
          </ul>
          
          <!-- Language Dropdown Desktop -->
          <div class="lang-dropdown nav-item dropdown d-none d-md-block ms-md-3 my-2 my-md-0" @mouseenter="showDropdownDesktop = true" @mouseleave="showDropdownDesktop = false">
            <button 
              class="nav-link dropdown-toggle lang-dropdown-btn d-flex align-items-center"
              @click="toggleDropdownDesktop"
              aria-expanded="false"
            >
              <i class="fa-solid fa-globe me-2"></i>
              <span>{{ currentLocale.toUpperCase() }}</span>
              <i class="fa-solid fa-chevron-down ms-2 lang-chevron" :class="{ rotated: showDropdownDesktop }"></i>
            </button>
            <ul class="dropdown-menu lang-dropdown-menu" :class="{ show: showDropdownDesktop }">
              <li>
                <button class="dropdown-item" :class="{ active: currentLocale === 'en' }" @click="selectLangDesktop('en')">EN</button>
              </li>
              <li>
                <button class="dropdown-item" :class="{ active: currentLocale === 'fr' }" @click="selectLangDesktop('fr')">FR</button>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </nav>
  </header>

  <div v-if="isHome" class="home-banner-wrapper">
    <banner></banner>
  </div>
</template>

<script>
import banner from '../UI/Banner.vue';
import { ref, computed } from 'vue';
import { useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { loadLocaleMessages } from '../../main.js';

export default {
  components: { banner },
  setup() {
    const isCollapsed = ref(false); 
    const route = useRoute();
    const { locale } = useI18n();
    const showDropdownDesktop = ref(false);
    const showDropdownMobile = ref(false);

    const currentLocale = computed(() => locale.value);

    const changeLang = async (lang) => {
      await loadLocaleMessages(lang);
      locale.value = lang;
      localStorage.setItem('locale', lang);
    };

    const toggleDropdownDesktop = () => {
      showDropdownDesktop.value = !showDropdownDesktop.value;
    };

    const toggleDropdownMobile = () => {
      showDropdownMobile.value = !showDropdownMobile.value;
    };

    const selectLangDesktop = async (lang) => {
      await changeLang(lang);
      showDropdownDesktop.value = false;
    };

    const selectLangMobile = async (lang) => {
      await changeLang(lang);
      showDropdownMobile.value = false;
    };

    const isHome = computed(() => {
      return route.path === '/Thanks' || route.path === '/SignIn';
    });

    const toggleCollapse = () => {
      isCollapsed.value = !isCollapsed.value;
      document.body.classList.toggle('no-scroll', isCollapsed.value);
    };

    const closeMenu = () => {
      isCollapsed.value = false;
      document.body.classList.remove('no-scroll');
    };

    return { 
      isCollapsed, 
      toggleCollapse, 
      closeMenu, 
      isHome,
      currentLocale,
      changeLang,
      showDropdownDesktop,
      showDropdownMobile,
      toggleDropdownDesktop,
      toggleDropdownMobile,
      selectLangDesktop,
      selectLangMobile
    };
  },
};
</script>

<style scoped>
/* Dropdown wrapper styling */
.nav-item.dropdown {
  position: relative;
}

.dropdown-toggle::after {
  display: inline-block;
  margin-left: 0.3em;
  vertical-align: middle;
  content: "";
  border-top: 0.3em solid;
  border-right: 0.3em solid transparent;
  border-bottom: 0;
  border-left: 0.3em solid transparent;
  transition: transform 0.2s ease;
}

.dropdown-toggle.active::after,
.dropdown-toggle:hover::after {
  border-top-color: #2584C5 !important;
}

.dropdown-menu {
  display: none;
  position: absolute;
  top: 100%;
  left: 50%;
  transform: translateX(-50%);
  background-color: #ffffff;
  min-width: 160px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.08);
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 0.5rem 0;
  margin-top: 0;
  z-index: 1000;
  list-style: none;
}

/* Bridge the gap between parent nav item and dropdown menu to prevent hover loss */
.dropdown-menu::before {
  content: '';
  position: absolute;
  top: -15px;
  left: 0;
  right: 0;
  height: 15px;
  background: transparent;
}

.dropdown-menu.show {
  display: block;
  animation: dropdownFadeIn 0.2s ease;
}

.dropdown-item {
  display: block;
  width: 100%;
  padding: 0.6rem 1.5rem;
  clear: both;
  font-weight: 500;
  color: #717d96;
  text-align: center;
  white-space: nowrap;
  background-color: transparent;
  border: 0;
  transition: all 0.2s ease;
  font-size: 15px;
  text-decoration: none !important;
}

.dropdown-item:hover, .dropdown-item:focus {
  color: #2584C5 !important;
  background-color: #f8fafc;
  text-decoration: none !important;
}

.dropdown-item.active, .dropdown-item:active {
  color: #2584C5 !important;
  background-color: #f1f5f9;
  font-weight: 700;
}

@keyframes dropdownFadeIn {
  from {
    opacity: 0;
    transform: translate(-50%, 10px);
  }
  to {
    opacity: 1;
    transform: translate(-50%, 0);
  }
}

/* Pure CSS hover behavior for desktop window viewports */
@media screen and (min-width: 768px) {
  .nav-item.dropdown:hover .dropdown-menu {
    display: block !important;
  }
}

@media screen and (max-width: 767px) {
  .dropdown-menu {
    position: static;
    float: none;
    transform: none;
    box-shadow: none;
    border: none;
    background-color: #f8fafc;
    margin-top: 0.25rem;
    padding: 0.5rem 0;
    width: 100%;
    animation: none;
  }
  
  .dropdown-menu.show {
    display: block;
  }

  .dropdown-item {
    text-align: center;
    padding: 0.75rem 1.5rem;
  }

  /* Language Dropdown Mobile Overrides */
  .lang-dropdown-menu {
    left: auto !important;
    right: 0 !important;
    transform: none !important;
    position: absolute !important;
    float: none !important;
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.08) !important;
    border: 1px solid #e5e7eb !important;
    background-color: #ffffff !important;
    margin-top: 5px !important;
    padding: 0.5rem 0 !important;
    width: 100px !important;
    min-width: 100px !important;
    animation: dropdownFadeInMobile 0.2s ease !important;
  }
}

/* Language Dropdown styling */
.lang-dropdown {
  position: relative;
}

.lang-dropdown-btn {
  background: none;
  border: none;
  font-size: 16px;
  font-weight: 500;
  color: #333333 !important;
  padding: 0;
  cursor: pointer;
  transition: color 0.2s ease;
  display: flex;
  align-items: center;
}

/* Hide duplicate caret from bootstrap dropdown-toggle */
.lang-dropdown-btn::after {
  display: none !important;
}

.lang-dropdown-btn:hover {
  color: #2584C5 !important;
}

.lang-chevron {
  font-size: 11px;
  transition: transform 0.2s ease;
}

.lang-dropdown:hover .lang-chevron,
.lang-chevron.rotated {
  transform: rotate(180deg);
}

.lang-dropdown-menu {
  display: none;
  position: absolute;
  top: 100%;
  left: 50%;
  transform: translateX(-50%);
  background-color: #ffffff;
  min-width: 100px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.08);
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 0.5rem 0;
  margin-top: 5px;
  z-index: 1000;
  list-style: none;
}

.lang-dropdown-menu.show {
  display: block;
  animation: dropdownFadeIn 0.2s ease;
}

.lang-dropdown-menu .dropdown-item {
  display: block;
  width: 100%;
  padding: 0.5rem 1.5rem;
  clear: both;
  font-weight: 500;
  color: #717d96;
  text-align: center;
  white-space: nowrap;
  background-color: transparent;
  border: 0;
  transition: all 0.2s ease;
  font-size: 14px;
  text-decoration: none !important;
}

.lang-dropdown-menu .dropdown-item:hover {
  color: #2584C5 !important;
  background-color: #f8fafc;
}

.lang-dropdown-menu .dropdown-item.active {
  color: #2584C5 !important;
  background-color: #f1f5f9;
  font-weight: 700;
}

@keyframes dropdownFadeInMobile {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>
